<template>
    <Modal :show="true" class="reservations__schedule__book-slot" @close="close">
        <form class="flex flex-col space-y-4" @submit.prevent="submit">
            <h3 class="text-lg font-semibold">
                {{ lang('reservations::schedule.modals.book_slot.title') }}
            </h3>
            <div
                class="px-6 py-3 -mx-6 space-y-4 border-gray-300 bg-gray-50 border-y dark:bg-zinc-800 dark:border-zinc-900"
            >
                <div class="flex gap-4">
                    <div class="grow-0 font-medium">
                        {{ lang('reservations::reservations.fields.slottable') }}
                    </div>
                    <div class="flex-auto">
                        {{ props.reservationSlot.name }}
                    </div>
                </div>

                <div v-show="props.showAdvanced">
                    <Field
                        v-model="form.reservation_metable_type"
                        :field="{
                            name: lang('reservations::reservations.meta.fields.reservation_metable_type'),
                            slug: 'reservation_metable_type',
                            adminComponent: 'AdminSelect',
                            type: 'select',
                        }"
                        dusk="form-reservation_metable_type"
                        :options="metableOptions"
                        label-position="left"
                    ></Field>
                </div>
                <div class="space-y-4">
                    <Field
                        v-model="form.customData.name"
                        :field="{
                            name: lang('reservations::reservations.meta.fields.name'),
                            slug: 'name',
                            type: 'text',
                            adminComponent: 'AdminText',
                            placeholder: lang('reservations::reservations.meta.fields.name'),
                        }"
                        dusk="form-name"
                        label-position="left"
                    ></Field>
                    <Field
                        v-model="form.customData.email"
                        :field="{
                            name: lang('reservations::reservations.meta.fields.email'),
                            slug: 'email',
                            type: 'email',
                            adminComponent: 'AdminEmail',
                            placeholder: lang('reservations::reservations.meta.fields.email'),
                        }"
                        dusk="form-email"
                        label-position="left"
                    ></Field>
                    <Field
                        v-model="form.customData.phone"
                        :field="{
                            name: lang('reservations::reservations.meta.fields.phone'),
                            slug: 'phone',
                            type: 'tel',
                            adminComponent: 'AdminTel',
                            placeholder: lang('reservations::reservations.meta.fields.phone'),
                        }"
                        dusk="form-phone"
                        label-position="left"
                    ></Field>

                    <div>
                        <table
                            v-if="props.selectColor"
                            class="border border-collapse border-gray-300 dark:border-neutral-900 [&_td]:border [&_td]:border-gray-300 [&_td]:dark:border-neutral-900 w-full [&_th]:border [&_th]:border-gray-300 [&_th]:dark:border-neutral-900 mt-4 text-sm rounded shadow"
                        >
                            <thead>
                                <tr>
                                    <th colspan="3" class="px-3 py-2 text-left bg-gray-100 dark:bg-zinc-800">
                                        {{ lang('reservations::reservations.meta.fields.color') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="entry in [
                                        { color: 'red', label: 'Dvořák ml.' },
                                        { color: 'purple', label: 'Dvořák st.' },
                                        { color: 'blue', label: 'Firmy' },
                                        { color: 'green', label: 'Zaměstnanci' },
                                        { color: 'yellow', label: 'Výchozí' },
                                    ]"
                                    :key="entry.color"
                                    :class="[
                                        entry.color === form.customData.color
                                            ? 'bg-gray-100 dark:bg-zinc-800'
                                            : 'bg-white dark:bg-neutral-950',
                                    ]"
                                >
                                    <td class="px-2 py-1 text-center" width="50">
                                        <AdminRadio
                                            :id="`color-${entry.color}`"
                                            v-model="form.customData.color"
                                            :value="`${entry.color}`"
                                        />
                                    </td>
                                    <td class="px-2 py-1 text-center" width="50">
                                        <label :for="`color-${entry.color}`" class="cursor-pointer">
                                            <span
                                                class="block w-6 h-6 mx-auto rounded-full"
                                                :class="`bg-${entry.color}-600`"
                                            ></span>
                                        </label>
                                    </td>
                                    <th class="pl-2 text-left">
                                        <label :for="`color-${entry.color}`" class="block cursor-pointer">{{
                                            entry.label
                                        }}</label>
                                    </th>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <fieldset class="px-4 py-2">
                <div class="flex space-x-8">
                    <div class="flex-auto text-center">
                        <Button
                            type="submit"
                            :class="{ 'opacity-75': form.processing }"
                            :loading="form.processing"
                            :disabled="form.processing"
                            dusk="form-submit"
                            color="primary"
                        >
                            {{ lang('reservations::schedule.actions.book') }}
                        </Button>
                    </div>
                </div>
            </fieldset>
        </form>
    </Modal>
</template>

<script setup lang="ts">
import { toRef, computed } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { useToast } from '@src/Common/Composables/useToast';

interface Props {
    reservationSlot?: any;
    metas?: {
        [key: string]: string;
    } | null;
    showAdvanced?: boolean;
    selectColor?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    reservationSlot: {},
    metas: null,
    showAdvanced: false,
    selectColor: false,
});

const emit = defineEmits(['close', 'booked']);
const metas = toRef(props, 'metas');

const close = () => {
    emit('close');
};

const form = useForm({
    id: props.reservationSlot?.id,
    reservable_id: props.reservationSlot?.reservable_id,
    reservable_type: props.reservationSlot?.reservable_type,
    reservation_metable_type: 'detail',
    customData: {},
    confirmed_at: new Date().toISOString(),
    confirmed: 1,
});

const submit = () => {
    form.post(route('reservations.slots.book'), {
        preserveScroll: true,
        preserveState: true,
        forceFormData: true,
        onSuccess: () => emit('booked'),
        onStart: () => ({}),
        onFinish: () => emit('close'),
        onError: (data) => {
            Object.values(data).forEach((message) => {
                useToast({ message: message, type: 'error' });
            });
        },
    });
};

const metableOptions = computed(() => {
    if (!metas.value) {
        return [];
    }

    return Object.keys(metas.value).map((key) => ({
        id: key,
        name: metas.value[key].name,
    }));
});
</script>
